import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Lock } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { SKIN_TEASERS, FREE_SKINS } from "../lib/theme";
import SkinPreview from "../components/SkinPreview";
import ServicePicker from "../components/ServicePicker";
import Wordmark from "../components/Wordmark";
import { toast } from "sonner";

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, patchUser } = useAuth();
  const { skin, setSkin } = useTheme();
  const [svcMeta, setSvcMeta] = useState({ services: [], grouped: false, region_label: "United States" });
  const [selected, setSelected] = useState(new Set(user?.services || []));
  const [saving, setSaving] = useState(false);
  const availableSkins = SKIN_TEASERS.filter((s) => s.available);

  useEffect(() => {
    const region = user?.region || "US";
    api.get("/meta/services", { params: { region } }).then(({ data }) => setSvcMeta(data)).catch(() => {});
  }, [user?.region]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const finish = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/user/onboard", { services: Array.from(selected) });
      patchUser({ services: data.services, onboarded: true });
      navigate("/");
    } catch {
      toast.error("Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-base)] px-4 py-14">
      <div className="mx-auto max-w-3xl text-center fade-up">
        <span className="flex justify-center" data-testid="onboarding-brand">
          <Wordmark sizeClass="text-2xl" />
        </span>
        <h1 className="mt-6 font-head text-4xl sm:text-5xl tracking-wide text-[var(--text-primary)]">Which do you pay for?</h1>
        <p className="mt-3 text-[var(--text-secondary)]">Pick your subscriptions. We'll surface what's new across all of them and point you to the right app to watch. You can change this anytime in Settings. Only select services you currently pay for.</p>
      </div>

      <div className="mx-auto my-10 max-w-3xl">
        <p className="mb-3 text-center text-xs uppercase tracking-wider text-[var(--text-muted)]" data-testid="onboarding-services-region">Services shown for {svcMeta.region_label}</p>
        <ServicePicker services={svcMeta.services} grouped={svcMeta.grouped} selected={selected} toggle={toggle} testidPrefix="service-tile" heightClass="h-28" />
      </div>

      <div className="mx-auto max-w-3xl">
        <h2 className="font-head text-2xl sm:text-3xl text-[var(--text-primary)]">Choose your look</h2>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">Pick an interface era — tap to preview it live. You can switch anytime in Settings.</p>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {availableSkins.map((s) => {
            const active = skin === s.id;
            const locked = !FREE_SKINS.includes(s.id) && !user?.full_access;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => {
                  if (locked) { toast("Full Access skin — request it anytime in Settings."); return; }
                  setSkin(s.id); toast.success(`${s.label} look applied`);
                }}
                className={`group overflow-hidden rounded-[var(--card-radius)] border p-3 text-left transition-all ${
                  active ? "border-[var(--accent)] ring-1 ring-[var(--accent)]" : "border-[var(--border-subtle)] hover:border-[var(--accent)]"
                } ${locked ? "opacity-80" : ""}`}
                data-testid={`onboarding-skin-${s.id}`}
              >
                <div className="relative">
                  <SkinPreview skinId={s.id} />
                  {locked && <div className="absolute inset-0 flex items-center justify-center rounded-sm bg-black/45 backdrop-blur-[1px]"><Lock size={18} className="text-white" /></div>}
                </div>
                <div className="mt-2 flex items-center gap-1.5">
                  <span className="font-head text-lg text-[var(--text-primary)]">{s.label}</span>
                  {active && <Check size={15} className="text-[var(--accent)]" />}
                  {locked && <span className="ml-auto inline-flex items-center gap-1 rounded bg-[var(--text-muted)]/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[var(--text-secondary)]"><Lock size={9} /> Full Access</span>}
                </div>
                <p className="text-[11px] uppercase tracking-wider text-[var(--text-muted)]">{s.era}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mx-auto mt-10 flex max-w-3xl items-center justify-between">
        <span className="text-sm text-[var(--text-secondary)]" data-testid="onboarding-count">{selected.size} selected</span>
        <button
          onClick={finish}
          disabled={saving}
          className="rounded-md bg-[var(--accent)] px-8 py-3 font-bold uppercase tracking-wide text-white transition-colors hover:bg-[var(--accent-hover)] disabled:opacity-60"
          data-testid="onboarding-continue-button"
        >
          {saving ? "Saving..." : selected.size ? "Continue" : "Skip for now"}
        </button>
      </div>
    </div>
  );
}
